import { GetRepairFormsRequestDTO, GetRepairsRequestDTO } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const repairKeys = {
  all: ['repairs'] as const,
  detail: (repairId: string | undefined, isAWA: boolean) => [...repairKeys.all, 'detail', repairId, isAWA] as const,
  revisions: (repairId: string | undefined, isAWA: boolean) => [...repairKeys.all, 'revisions', repairId, isAWA] as const,
  infinite: (filters?: GetRepairsRequestDTO['filters'], groupBy?: GetRepairsRequestDTO['groupBy'], orderBy?: GetRepairsRequestDTO['orderBy']) =>
    [...repairKeys.all, 'infinite', filters, groupBy, orderBy] as const,
};

export const repairFormKeys = {
  all: ['repair-forms'] as const,
  infinite: (filters?: GetRepairFormsRequestDTO['filters'], orderBy?: GetRepairFormsRequestDTO['orderBy']) =>
    [...repairFormKeys.all, 'infinite', filters, orderBy] as const,
  detail: (formRevisionId: string | null | undefined, isAWA: boolean) => [...repairFormKeys.all, 'detail', formRevisionId, isAWA] as const,
};

export const getRepairByIdQueryOptions = ({ repairId, isAWA = false }: { repairId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: repairKeys.detail(repairId, isAWA),
    queryFn: async () => {
      if (!repairId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetRepairById({ repairId })).json()).repair;
    },
    enabled: !!repairId,
  });
};

export const getRepairRevisionsQueryOptions = ({ repairId, isAWA = false }: { repairId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: repairKeys.revisions(repairId, isAWA),
    queryFn: async () => {
      if (!repairId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetRepairRevisions({ repairId })).json()).revisions;
    },
    enabled: !!repairId,
  });
};

export const getRepairsInfiniteQueryOptions = ({
  filters,
  groupBy,
  orderBy,
  enabled = true,
}: {
  filters?: GetRepairsRequestDTO['filters'];
  groupBy?: GetRepairsRequestDTO['groupBy'];
  orderBy?: GetRepairsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: repairKeys.infinite(filters, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetRepairs({ limit: 30, cursors: pageParam, filters, groupBy, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getRepairFormsInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetRepairFormsRequestDTO['filters'];
  orderBy?: GetRepairFormsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: repairFormKeys.infinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetRepairForms({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getRepairFormQueryOptions = ({ formRevisionId, isAWA = false }: { formRevisionId: string | null | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: repairFormKeys.detail(formRevisionId, isAWA),
    queryFn: async () => {
      if (!formRevisionId) return null;
      return (await (await (isAWA ? AWAApiV2Service : ApiService).GetFormRevisionById({ formRevisionId })).json()).formRevision;
    },
    enabled: !!formRevisionId,
  });
};
